import type * as React from 'react';
import Input from '@cloudscape-design/components/input';
import { type Currency, digitsToCents, formatCentsMasked } from '@etiquetachef/types';

/** Props do `CurrencyInput`; o valor trafega sempre em centavos inteiros. */
type Props = {
  /** Valor em CENTS inteiros; `null` = vazio. */
  cents: number | null;
  onChange: (cents: number | null) => void;
  currency: Currency;
  disabled?: boolean;
  invalid?: boolean;
  /** Teto de dígitos (default 11 → até 999.999.999,99). */
  maxDigits?: number;
  ariaLabel?: string;
};

/**
 * Campo de valor monetário do admin (Cloudscape não tem componente de moeda
 * nativo — este é o nosso).
 *
 * Origem: sem issue — commit 5e7d598c6, 2026-06-22 (valor de cupom).
 *
 * Por quê: máscara "centavos da direita para a esquerda" — o operador digita
 * só dígitos e os centavos preenchem a partir da direita (digitar `99800` →
 * `998,00`). Como não há parse de separador decimal, é impossível inserir um
 * valor ambíguo: pt-BR (`1.998,00`) e en-US (`1,998.00`) não conflitam, e não
 * há como gravar um valor 1000× errado (camada de segurança pedida pelo owner
 * em 2026-06-22).
 *
 * Contrato: fonte da verdade é `cents` (inteiro, `null` = vazio); o caller
 * nunca divide por 100. `onChange` recebe o resultado de `digitsToCents`,
 * truncado em `maxDigits` (default 11 → até 999.999.999,99).
 *
 * Usado por: `features/sellerEntities/SellerEntityEditorPage.tsx` (teto anual
 * de faturamento, `annual_cap_amount_cents`). O diálogo de cupom que o originou saiu. Teste:
 * `CurrencyInput.test.tsx`.
 *
 * Arquitetura: a máscara mora em `@etiquetachef/types` (`currencyMask.ts`)
 * desde 2026-09-29 (#776); o CRM tem um wrapper gêmeo
 * (`apps/crm/src/components/CurrencyInput.tsx`) sobre a mesma lógica — mudança
 * de comportamento vai no pacote, não aqui.
 *
 * Issues: #776 (máscara compartilhada com o CRM).
 *
 * Histórico:
 *  - 2026-10-10 13:37 +0200 — (#926) "Usado por" corrigido: dizia que só o
 *    teste consumia, mas a tela de entidade vendedora usa o campo.
 */
export function CurrencyInput({
  cents,
  onChange,
  currency,
  disabled,
  invalid,
  maxDigits = 11,
  ariaLabel,
}: Props): React.JSX.Element {
  return (
    <Input
      inputMode="numeric"
      value={cents == null ? '' : formatCentsMasked(cents, currency)}
      placeholder={formatCentsMasked(0, currency)}
      disabled={disabled ?? false}
      invalid={invalid ?? false}
      {...(ariaLabel != null ? { ariaLabel } : {})}
      onChange={({ detail }): void => {
        onChange(digitsToCents(detail.value, maxDigits));
      }}
    />
  );
}
